import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { QICustomSelect, QIButton } from "../../../../components";
import {
  useGetEmailNotificationConfigurationsQuery,
  useGetEventNotificationConfigurationsQuery,
  useGetUserGroupsQuery,
  useCreateEmailNotificationConfigurationMutation,
  useUpdateEmailNotificationConfigurationMutation,
} from "../../services";
import { SvgIcon } from "../../../Shared";
import { MPSharedHeader } from "../../Shared";
import { getLocalizedString } from "../../../../shared/translation";
import { CurrentUser } from "../../../../shared/currentUser";
import { ScheduledReportDelivery } from "./ScheduledReportDelivery";
import { FMDPStorage, parseServerError } from "../../../../shared/helper";
import { EventEmailDeliveryCard } from "./EventEmailDeliveryCard";

export const EmailNotificationConfig = () => {
  const dropdownParams = { per_page: 1000, order_by: "name" };
  const [editMode, setEditMode] = useState(false);

  const currentUser = new CurrentUser("Email Notification Configurations");

  // The current client scopes the event notification configuration requests.
  const clientId = FMDPStorage.get("selected-client-id");

  // Fetch subscription email notification configuration
  const { data: configData } = useGetEmailNotificationConfigurationsQuery({});

  // Fetch the event notification configuration for the current client. When
  // the collection is empty the Event Email Delivery form renders empty and
  // submit creates via POST; when a record exists it pre-fills and submit
  // updates via PUT.
  const { data: eventConfigData, error: eventConfigError } =
    useGetEventNotificationConfigurationsQuery({ client_id: clientId });

  // Fetch user groups
  const { data: userGroups, isSuccess: isUserGroupsSuccess } =
    useGetUserGroupsQuery(dropdownParams);

  // Create mutation
  const [
    createConfig,
    {
      error: createError,
      isSuccess: createSuccess,
    },
  ] = useCreateEmailNotificationConfigurationMutation();

  // Update mutation
  const [
    updateConfig,
    {
      error: updateError,
      isSuccess: updateSuccess,
    },
  ] = useUpdateEmailNotificationConfigurationMutation();

  // The subscription notification record for this client (first, if any).
  const configs = configData?.data ?? [];
  const existingConfig = Array.isArray(configs) ? configs[0] : configs;

  // The event notification configuration record for this client (first, if
  // any). A missing record means the Event Email Delivery form renders empty.
  const eventConfigs = eventConfigData?.data ?? [];
  const eventEmailConfig = Array.isArray(eventConfigs)
    ? eventConfigs[0] ?? null
    : eventConfigs ?? null;

  const handleSubmit = (formData) => {
    const payload = {
      type: "subscription_expiry_notification",
      user_group_id: parseInt(formData.user_group_id),
    };

    if (existingConfig?.id) {
      // Update existing configuration
      updateConfig({
        id: existingConfig.id,
        formData: payload,
      });
    } else {
      // Create new configuration
      createConfig({
        formData: payload,
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      user_group_id: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => handleSubmit(values),
  });

  // Pre-select saved user group when configuration exists
  useEffect(() => {
    if (existingConfig?.user_group_id) {
      formik.setFieldValue("user_group_id", existingConfig.user_group_id);
    }
  }, [existingConfig]);

  // Handle server errors
  useEffect(() => {
    const error = createError || updateError;
    if (error) {
      if (error.status === 500 || error.status === 404) {
        toast.error(
          getLocalizedString(
            "email_notification_config_error",
            "Something went wrong. Please try again later."
          )
        );
      } else {
        formik.setErrors(parseServerError(error));
      }
    }
  }, [createError, updateError]);

  // Handle success - exit edit mode
  useEffect(() => {
    if (createSuccess || updateSuccess) {
      setEditMode(false);
    }
  }, [createSuccess, updateSuccess]);

  const toggleEditMode = () => {
    if (editMode) {
      // Reset to saved value on cancel
      formik.setFieldValue(
        "user_group_id",
        existingConfig?.user_group_id || ""
      );
      setEditMode(false);
    } else {
      setEditMode(true);
    }
  };

  return (
    <article className="main-container">
      <MPSharedHeader
        groupName="client_config"
        heading={getLocalizedString("settings", "Settings")}
        resourceName={getLocalizedString("email_notification_configuration", "Email Notification Configuration")}
      />
    <div className="email-notification-config-wrapper">
      <div className="customization_block subscription-notification-card">
        <header className="customization_block_header">
          <strong>
            {getLocalizedString("subscription_notification", "Subscription Notification")}
          </strong>
          {currentUser.canUpdate && (
            <span className="edit">
              <SvgIcon name="edit" wrapperClass="mx-2" onClick={toggleEditMode} />
            </span>
          )}
        </header>
        <div>
          <QICustomSelect
            label={getLocalizedString("user_group", "User Group")}
            value={formik.values.user_group_id}
            onChange={(value) => formik.setFieldValue("user_group_id", value)}
            disabled={!editMode}
            readOnly={!editMode}
            error={formik.touched.user_group_id && formik.errors.user_group_id}
          >
            <li value="">
              {getLocalizedString("select_user_group", "Select User Group")}
            </li>
            {isUserGroupsSuccess &&
              userGroups?.data?.map((group) => (
                <li key={group.id} value={group.id}>
                  {group.name}
                </li>
              ))}
          </QICustomSelect>

          {formik.errors.serverError && (
            <div className="text-danger mt-2">{formik.errors.serverError}</div>
          )}

          {editMode && (
            <div className="user-profile-details_footer pt-4">
              <QIButton className="secondary sm mr-2" onClick={toggleEditMode}>
                {getLocalizedString("cancel", "Cancel")}
              </QIButton>
              <QIButton className="primary sm" onClick={formik.handleSubmit}>
                {getLocalizedString("update", "Update")}
              </QIButton>
            </div>
          )}
        </div>
      </div>

      <EventEmailDeliveryCard config={eventEmailConfig} loadError={eventConfigError} />

      <ScheduledReportDelivery />
    </div>
    </article>
  );
};
